import { ApolloClient, ApolloProvider, NormalizedCacheObject } from '@apollo/client'
import * as React from 'react'

import { TLocaleOptions } from '../../../config/locales'
import { useAuthCtx, useAuthStateCtx } from '../auth/AuthProvider'
import { TActionCableClient } from './ActionCableClient'
import { createGqlClient } from './createClient'

type TProps = RequiredChildren & {
  backendUrl: string
  activeLocale: TLocaleOptions['locale']
}

/**
 * GQL BE connection provider
 * currently Apollo client context that allows us to use all of the GQL operations we write (Q/M/S) in the component tree bellow this provider
 */
export const ClientGqlProvider: React.FC<TProps> = ({ children, activeLocale, backendUrl }) => {
  const { authToken } = useAuthCtx()
  const authState = useAuthStateCtx()

  const [apolloClient, setApolloClient] =
    React.useState<Nullable<ApolloClient<NormalizedCacheObject>>>(null)
  const [cableClient, setCableClient] = React.useState<Nullable<TActionCableClient>>(null)

  const refPrevAuthToken = React.useRef<Nullable<string>>(null)
  const refPrevLocale = React.useRef<Nullable<string>>(null)

  // * INIT ActionCableClient
  // the actioncable lib will break when imported outside of the browser (eg. on SS)
  // that’s why the ActionCableClient (that imports that lib) can’t be imported the regular way
  // – as React effect are run only on the client side, it’s safe to import in inside that instead
  React.useEffect(() => {
    // we want to reinitialize the client only on token/locale change
    // TODO do we need to reinitialize the client or just update the headers via the setContext link?
    // TODO see the contributor’s comment here https://github.com/apollographql/apollo-client/issues/11096#issuecomment-1649923608
    // ! the same is true for the apollo client created in the effect below
    // these two effects run one after another,
    // therefore the ref is updated in the second one
    // after it is used in both conditions (in both effects)
    const isSameToken = refPrevAuthToken.current !== null && refPrevAuthToken.current === authToken

    if (isSameToken) return

    // import, create & save the client
    const initActionCableClient = async () => {
      if (!authToken) return

      const { ActionCableClient } = await import('./ActionCableClient')

      const clientInstance = new ActionCableClient({
        endpointUrl: backendUrl,
        authToken,
      })

      setCableClient(clientInstance)
    }

    initActionCableClient()
  }, [authToken, backendUrl, cableClient])

  // * CREATE APOLLO CLIENT
  // create APOLLO GQL client once all data is available

  React.useEffect(() => {
    // first we need to initialize the user (token)
    if (!authState.isValid || !authToken) return

    // we also want to have the actioncable needed for S ready
    if (!cableClient) return

    // we want to reinitialize the client only on token/locale change
    // we need the client to contain the valid headers
    // based on the `activeLocale` and the valid `authToken`
    const isSameToken = refPrevAuthToken.current !== null && refPrevAuthToken.current === authToken
    const isSameLocale = refPrevLocale.current !== null && refPrevLocale.current === activeLocale

    if (isSameToken && isSameLocale) return

    // store new values as refs to be used on the next run
    refPrevAuthToken.current = authToken
    refPrevLocale.current = activeLocale

    // Use the new splitLink for the Apollo client
    const gqlClient = createGqlClient({
      authToken,
      backendUrl,
      locale: activeLocale,
      actionCableClient: cableClient,
    })

    setApolloClient(gqlClient)
  }, [activeLocale, authState.isValid, authToken, backendUrl, cableClient])

  // do not render on the server
  if (typeof window === undefined) {
    return <>{children}</>
  }

  if (!apolloClient) {
    return null
  }

  return <ApolloProvider client={apolloClient}>{children}</ApolloProvider>
}
